import React, { FC, useEffect, useState } from "react";

import "../styles.scss";
import "../scripts";

import {
  primaryBlock,
  PrimaryBlockConfig,
  getDefaultProps,
  KameleonElement,
  SecondaryBlockSize,
  TertiaryBlockSize,
  BlockTheme,
  BlockType,
} from "@kameleon-core/lib";
import { Box } from "theme-ui";

import { webflowHOC, WebflowComponentProps } from "@kameleon-common/webflow-js";
import { Variants } from "@kameleon-util/global-theme";
import { v4 as uuidv4 } from "uuid";
import blockinfo from "./blockinfo.json";
import defaults from "./defaults.json";
import { ContentType, layoutOptions } from "./types/prop-types";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import getEvents from "./js/webflow";

/** Content Primary */
const blockConfig: PrimaryBlockConfig = {
  secondaryBlockSize: SecondaryBlockSize.None,
  tertiaryBlockSize: TertiaryBlockSize.None,
  blockType: BlockType.Primary,
  blockTheme: BlockTheme.light,
};

type PassThroughProps = {
  hoverHeaderId: string;
};

type ContentWebflowType = WebflowComponentProps<ContentType, PassThroughProps>;

const ContentComponent: FC<ContentWebflowType> = ({
  items,
  tagline,
  layoutProps,
  passThrough,
}: ContentWebflowType) => {
  const [hoverHeaderId, _] = useState(uuidv4());

  useEffect(() => {
    passThrough?.({ hoverHeaderId });
  }, []);

  return (
    <Box className="primary-content-rows-image-on-hover-v1">
      <Box className="section_cta38">
        <Box className="padding-global">
          <Box className="container-large">
            <Box className="padding-section-large">
              {tagline && (
                <Box className="margin-bottom margin-medium">
                  <Variants.TagLine>{tagline}</Variants.TagLine>
                </Box>
              )}
              <Box variant="boxes.hoverContainer" className="cta38_component">
                {items.map((item, i) => {
                  const imageProps = {
                    src: item.image.url,
                    alt: item.image.alt,
                    className: "cta38_link-image",
                  };

                  return (
                    <Variants.LinkHoverHeader
                      key={i}
                      data-w-id={hoverHeaderId}
                      href={item.link}
                      className="cta38_link-block w-inline-block"
                    >
                      {layoutProps.showItemNumber && (
                        <Box
                          variant="paragraph.primaryNumber"
                          className="cta38_link-text-small"
                        >
                          {(i + 1).toLocaleString("en-US", {
                            minimumIntegerDigits: 2,
                            useGrouping: false,
                          })}
                        </Box>
                      )}
                      <Variants.Heading className="cta38_link-text">
                        {item.text}
                      </Variants.Heading>
                      <Box
                        sx={{
                          marginLeft:
                            layoutProps.textAlignment === "center"
                              ? "60%"
                              : "50%",
                        }}
                        className="cta38_link-image-wrapper"
                      >
                        {layoutProps.mediaShape === "square" ? (
                          <Variants.Image {...imageProps} />
                        ) : (
                          <Variants.ImageCircle {...imageProps} />
                        )}
                      </Box>
                    </Variants.LinkHoverHeader>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ComponentWithWebflow = webflowHOC<ContentWebflowType, PassThroughProps>(
  ContentComponent,
  [
    {
      module: "ix2",
      getEvents: (pageId: any, passThrough): any =>
        getEvents(pageId, passThrough?.hoverHeaderId),
    },
  ]
);

export const Content = primaryBlock<ContentType>(
  blockConfig,
  ComponentWithWebflow
);
Content.defaultProps = getDefaultProps<ContentType>(defaults);

export const Element: KameleonElement = {
  component: Content,
  blockInfo: blockinfo,
  defaultProps: {
    layoutProps: defaults.layoutProps,
    contentProps: defaults.contentProps,
  },
  layoutOptions,
};
